/**
 * Debug Logging Utilities
 *
 * Usage (persists across navigation via localStorage):
 *   ?Debug=true     - all debug messages will be shown
 *   ?Debug=tealium  - only Tealium debug messages will be shown
 *   ?Debug=cohesion - only Cohesion debug messages will be shown
 *
 * Or via console:
 *   localStorage.setItem('Debug', 'cohesion')
 *   localStorage.removeItem('Debug')  // to disable
 */

/**
 * @typedef {() => void} MessageFunction
 * A function that logs message to dev tools console.
 */

/**
 * Gets the current debug flag value from feature flags or localStorage
 * @returns {string}
 */
function getDebugFlag() {
  if (typeof window === 'undefined') {
    return '';
  }

  // Try window._featureFlags first (set by FeatureFlagsProvider)
  const debug = window._featureFlags?.Debug;
  if (debug) {
    return String(debug).toLowerCase();
  }

  // Fall back to localStorage (in case _featureFlags not yet set)
  try {
    return (localStorage.getItem('Debug') || '').toLowerCase();
  } catch {
    return '';
  }
}

/**
 * @param {string} [scope] - Optional scope to match debug value
 * @returns {boolean}
 */
function shouldDebug(scope) {
  const debug = getDebugFlag();

  if (!debug) {
    return false;
  }

  if (['true', '*', '1'].includes(debug)) {
    return true;
  }

  // match specific scope
  return scope ? debug === scope.toLowerCase() : false;
}

/**
 * Runs the log function when browser URL enabled debugging
 * @param {MessageFunction} messageFunction
 * @param {string} [scope]
 */
export function conditionalDebug(messageFunction, scope) {
  if (shouldDebug(scope)) {
    messageFunction();
  }
}

/**
 * Runs the log function in non-prod environments or when browser URL enabled debugging
 * @param {MessageFunction} messageFunction
 * @param {string} [scope]
 */
function conditionalError(messageFunction, scope) {
  const isDev = process.env.NODE_ENV !== 'production';
  if (isDev || shouldDebug(scope)) {
    messageFunction();
  }
}

/**
 * Logs Tealium messages when debugging is enabled
 * @param {MessageFunction} messageFunction
 */
export const debugTealium = (messageFunction) =>
  conditionalDebug(messageFunction, 'tealium');

/**
 * Logs Cohesion messages when browser URL enabled debugging
 * @param {MessageFunction} messageFunction
 */
export const debugCohesion = (messageFunction) =>
  conditionalDebug(messageFunction, 'cohesion');

/**
 * Logs messages in non-prod environments
 * @param {MessageFunction} messageFunction
 */
export const debugError = (messageFunction) =>
  conditionalError(messageFunction);

/**
 * Logs Tealium errors in non-prod environments or when browser URL enabled debugging
 * @param {MessageFunction} messageFunction
 */
export const debugTealiumError = (messageFunction) =>
  conditionalError(messageFunction, 'tealium');

/**
 * Logs Cohesion errors in non-prod environments or when browser URL enabled debugging
 * @param {MessageFunction} messageFunction
 */
export const debugCohesionError = (messageFunction) =>
  conditionalError(messageFunction, 'cohesion');
